import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import VerdictBadge from "@/components/VerdictBadge";
import DealTypePill from "@/components/deal/DealTypePill";
import RoleDot from "@/components/deal/RoleDot";
import StageBadge from "@/components/pipeline/StageBadge";
import PipelineGlanceCard from "@/components/dashboard/PipelineGlanceCard";
import ReferFriendsWidget from "@/components/dashboard/ReferFriendsWidget";
import { PropertyDetailLine, PropertyDescription, PropertyContactLine } from "@/components/property/PropertyMeta";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useFmt } from "@/lib/useFmt";
import { formatScore, propertyTypeLabel } from "@/lib/format";
// Phase 47.2 — compact $/sqft subtitle on the Recent Deals price column.
import { formatPsfShort } from "@/lib/priceNormalisation";
import { LineChart, Plus, ArrowRight, Trash2, Sparkles } from "lucide-react";
import Skeleton from "@/components/ui/Skeleton";
import Sparkline from "@/components/ui/Sparkline";
import PortfolioPulseCard from "@/components/dashboard/PortfolioPulseCard";
import StageFilterDropdown, { stageFilterLabel } from "@/components/StageFilterDropdown";

function KpiCard({ label, value, sub, testid, tone = "default", spark, sparkColor }) {
  const toneCls =
    tone === "good"
      ? "border-emerald-200 bg-emerald-50/40"
      : tone === "bad"
      ? "border-red-200 bg-red-50/40"
      : "border-slate-200 bg-white";
  return (
    <div
      data-testid={testid}
      className={`rounded-xl border p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md ${toneCls}`}
    >
      <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">{label}</div>
      <div className="mt-2 text-2xl font-bold tracking-tight text-slate-900 tabular-nums">{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
      {Array.isArray(spark) && spark.length > 1 && (
        <div className="mt-3 -mb-1">
          <Sparkline data={spark} height={32} color={sparkColor} testid={`${testid}-spark`} />
        </div>
      )}
    </div>
  );
}

// Phase 45 Batch — Dashboard P0 fix (Recent Deals em-dash for flips).
// Flip rows must NOT render zeroed CF / Cap / CoC / Payback — flip
// economics live on ROI + Gross Profit, not on those rental fields.
// This helper renders an em-dash with a tooltip that explains where
// the user can find the right number (the deal page).
function FlipDash({ testid }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          data-testid={testid}
          className="cursor-help text-slate-400"
          aria-label="Not applicable for flip deals"
        >
          —
        </span>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        className="max-w-xs bg-slate-900 text-white"
        data-testid={`${testid}-tooltip`}
      >
        Flip economics use ROI &amp; Gross Profit — open the deal page to see them.
      </TooltipContent>
    </Tooltip>
  );
}

function EmptyState({ onCta, t }) {
  return (
    <div
      data-testid="empty-state-card"
      className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center shadow-sm"
    >
      <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
        <LineChart className="h-6 w-6" />
      </div>
      <h2 className="mt-4 text-xl font-semibold text-slate-900">
        {t("dashboard.empty_title")}
      </h2>
      <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">
        {t("dashboard.subtitle")}
      </p>
      <div className="mt-6">
        <Button
          data-testid="empty-state-cta"
          onClick={onCta}
          className="bg-indigo-600 text-white transition-all hover:-translate-y-0.5 hover:bg-indigo-700 hover:shadow-md"
        >
          <Plus className="mr-1 h-4 w-4" /> {t("common.new_deal_cta")}
        </Button>
      </div>
    </div>
  );
}

// Phase 13 P0 hotfix — safe defaults so the KPI cards never dereference null.
// If `/dashboard/summary` 4xx/5xx or returns null/undefined, we keep this
// shape so React renders the zero-state instead of crashing.
const SUMMARY_DEFAULT = {
  total_deals: 0,
  rental_deals_count: 0,
  total_monthly_cashflow: 0,
  avg_monthly_cashflow: 0,
  avg_cap_rate: 0,
  avg_deal_score: 0,
  best_verdict_count: 0,
  recent_deals: [],
};

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const fmt = useFmt();
  const [summary, setSummary] = useState(SUMMARY_DEFAULT);
  const [deals, setDeals] = useState([]);
  // Phase 33 — sparkline trend series; null while loading, never blocks UI.
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  // Phase 19 — filter chips. "all" / "owned" / "analyzing". Default "all".
  const [ownerFilter, setOwnerFilter] = useState("all");
  // Phase 36 Batch O — stage segmentation. Default mirrors backend
  // _DEFAULT_STAGE_DASHBOARD = "all_active" (Pipeline + Negotiating +
  // Under Contract + Owned, excluding sold + passed).
  const [stageFilter, setStageFilter] = useState("all_active");
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Phase 1 polish — smarter welcome fallback: if the user's first name
  // matches the brand (e.g. "RunIdea Admin" → "RunIdea"), substitute "Admin"
  // so the banner never reads "Welcome, RunIdea".
  const rawFirst = (user?.name || "").split(" ")[0];
  const firstName =
    rawFirst && rawFirst.toLowerCase() === "runidea"
      ? "Admin"
      : rawFirst || "there";

  const refresh = async () => {
    try {
      // Phase 36 Batch O — both endpoints honour the same ?stage= bucket
      // filter so the KPI cards + Recent Deals table stay aligned with
      // the dropdown selection above.
      const [s, d] = await Promise.all([
        api.get(`/dashboard/summary?stage=${stageFilter}`),
        api.get(`/deals?stage=${stageFilter}`),
      ]);
      setSummary({ ...SUMMARY_DEFAULT, ...(s.data || {}) });
      setDeals(Array.isArray(d.data) ? d.data : []);
    } catch {
      toast.error("Could not load your dashboard.");
      setSummary(SUMMARY_DEFAULT);
      setDeals([]);
    } finally {
      setLoading(false);
    }
    // Trends fetch is best-effort; KPI cards still render without it.
    try {
      const r = await api.get("/dashboard/trends");
      setTrends(r.data || {});
    } catch {
      setTrends({});
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stageFilter]);

  const onDelete = async () => {
    if (!pendingDeleteId) return;
    setDeleting(true);
    try {
      await api.delete(`/deals/${pendingDeleteId}`);
      toast.success("Deal deleted.");
      setPendingDeleteId(null);
      await refresh();
    } catch {
      toast.error("Failed to delete.");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <AppShell>
        <div className="space-y-10" data-testid="dashboard-loading">
          {/* Welcome heading skeleton */}
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="space-y-3">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-10 w-72" />
              <Skeleton className="h-4 w-96" />
            </div>
            <Skeleton className="h-10 w-32" />
          </div>
          {/* KPI strip skeleton — 4 placeholder cards */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Skeleton.Card />
            <Skeleton.Card />
            <Skeleton.Card />
            <Skeleton.Card />
          </div>
          {/* Recent deals table skeleton */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 px-6 py-4">
              <Skeleton className="h-4 w-32" />
            </div>
            <Skeleton.TableRows rows={4} cols={6} />
          </div>
        </div>
      </AppShell>
    );
  }

  const hasDeals = (summary?.total_deals || 0) > 0;
  const mostRecentAi = (deals || []).find((d) => d?.ai_summary?.headline);

  // Phase 19 — owned vs analyzing splits + portfolio appreciation rollup.
  // We use ONLY data already on each deal doc: is_owned, purchase_price_paid,
  // and the cached historical_performance snapshot when available. No extra
  // network round-trips — the dashboard stays as fast as before.
  const ownedDeals = (deals || []).filter((d) => d?.is_owned);
  const ownedCount = ownedDeals.length;
  // Phase 36 — Dashboard P0 follow-up. Track whether ANY owned deal has
  // a historical_performance snapshot. When none do, the appreciation KPI
  // renders as "—" instead of a misleading "+$0".
  const ownedDealsWithHistory = ownedDeals.filter(
    (d) => d?.historical_performance &&
      typeof d.historical_performance.total_appreciation === "number"
  );
  const hasAppreciationData = ownedDealsWithHistory.length > 0;
  const portfolioAppreciation = ownedDeals.reduce((acc, d) => {
    const hp = d?.historical_performance;
    if (hp && typeof hp.total_appreciation === "number") return acc + hp.total_appreciation;
    return acc;
  }, 0);
  const portfolioAppreciationPct = (() => {
    const totalCost = ownedDeals.reduce((acc, d) => acc + (Number(d?.purchase_price_paid) || 0), 0);
    return totalCost > 0 ? portfolioAppreciation / totalCost : null;
  })();
  // Phase 36 — Dashboard P0 follow-up. Aggregate monthly cash flow across
  // owned deals using the SAME role-aware fallback the Recent-Deals table
  // uses (line 549), so the KPI matches what the user reads per row.
  const ownedMonthlyCashFlow = ownedDeals.reduce((acc, d) => {
    const cf = d?.metrics_at_role?.monthly_cash_flow ?? d?.cash_flow_monthly;
    return acc + (Number(cf) || 0);
  }, 0);
  // Phase 36 — Dashboard P0 follow-up. Analyzing = anything not owned and
  // not sold. Matches the user's mental model that "still analyzing" means
  // "deals I'm still evaluating".
  const analyzingCount = (deals || []).filter(
    (d) => !d?.is_owned && (d?.pipeline_stage || "") !== "sold"
  ).length;

  return (
    <AppShell>
      <TooltipProvider delayDuration={150}>
      <div className="space-y-10" data-testid="dashboard-root">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-indigo-600">{t("nav.dashboard")}</p>
            <h1 className="mt-2 text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl" data-testid="dashboard-welcome">
              {t("dashboard.welcome", { name: firstName })}
            </h1>
            <p className="mt-3 max-w-2xl text-base text-slate-600">
              {t("dashboard.subtitle")}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <StageFilterDropdown
              value={stageFilter}
              onChange={setStageFilter}
              testid="dashboard-stage-filter"
            />
            <Button
              data-testid="header-new-deal-cta"
              onClick={() => navigate("/deals/new")}
              className="bg-indigo-600 text-white transition-all hover:-translate-y-0.5 hover:bg-indigo-700 hover:shadow-md"
            >
              <Plus className="mr-1 h-4 w-4" /> {t("common.new_deal_cta")}
            </Button>
          </div>
        </div>

        {/* Phase 36 Batch O — surface the active stage filter underneath
            the header so the user always knows which slice of their
            book they're looking at. Hidden for the default "all_active"
            value to keep the header uncluttered on first paint. */}
        {stageFilter && stageFilter !== "all_active" ? (
          <div
            data-testid="dashboard-stage-filter-banner"
            className="-mt-6 inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700"
          >
            <span>Showing: {stageFilterLabel(stageFilter)}</span>
            <button
              type="button"
              onClick={() => setStageFilter("all_active")}
              data-testid="dashboard-stage-filter-clear"
              className="text-indigo-600 hover:text-indigo-800"
            >
              clear ×
            </button>
          </div>
        ) : null}

        {/* Phase 35e — AI Portfolio Pulse: daily ambient AI commentary */}
        <PortfolioPulseCard />

        {/* KPI strip — guarded for null/missing summary fields. */}
        {/* Phase 45 Batch — rental-only KPI subtitle. We render
            "rentals only (X of Y deals)" under Monthly Cash Flow and
            Cap Rate so users instantly see that flips are excluded
            from these averages (flip economics live on ROI + Gross
            Profit, not on cap/CF). When the user has zero rentals,
            the value renders as "—" with a hint to add a rental. */}
        {(() => {
          const rentalCount = summary?.rental_deals_count ?? 0;
          const totalCount = summary?.total_deals ?? 0;
          const hasRentals = rentalCount > 0;
          const rentalSub = hasRentals
            ? `rentals only (${rentalCount} of ${totalCount} ${totalCount === 1 ? "deal" : "deals"})`
            : totalCount > 0
              ? "no rentals yet"
              : null;
          const cfValue = summary?.total_monthly_cashflow;
          const capValue = summary?.avg_cap_rate;
          const psfValue = summary?.avg_price_per_sqft;
          const psmValue = summary?.avg_price_per_sqm;
          return (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <KpiCard
                label={t("dashboard.kpis.deals")}
                value={summary?.total_deals ?? 0}
                testid="kpi-total-deals"
                spark={trends?.deals_analyzed_7d}
              />
              <KpiCard
                label={t("dashboard.kpis.monthly_cash_flow")}
                value={
                  hasRentals && cfValue != null
                    ? fmt.money(cfValue, { cents: true })
                    : "—"
                }
                sub={rentalSub}
                tone={
                  hasRentals && cfValue != null
                    ? cfValue >= 0
                      ? "good"
                      : "bad"
                    : "default"
                }
                testid="kpi-total-cashflow"
                spark={trends?.cash_flow_30d}
              />
              <KpiCard
                label={t("dashboard.table.cap_rate")}
                value={
                  hasRentals && capValue != null ? fmt.percent(capValue) : "—"
                }
                sub={rentalSub}
                testid="kpi-avg-cap"
              />
              {/* Phase 47.3 — portfolio-wide $/sqft chip. Rental-only
                  (mirrors the cap-rate contract). Sub line carries the
                  PSM mirror so LATAM/EU buyers see both metrics. */}
              <KpiCard
                label="Avg $/sqft"
                value={
                  hasRentals && psfValue != null
                    ? `$${Math.round(psfValue).toLocaleString("en-US")}`
                    : "—"
                }
                sub={
                  hasRentals && psmValue != null
                    ? `≈ $${Math.round(psmValue).toLocaleString("en-US")}/sqm`
                    : rentalSub
                }
                testid="kpi-avg-psf"
              />
            </div>
          );
        })()}

        {/* Phase 47.3 — secondary KPI row carrying the Avg Score chip the
            top strip used to own (we kept the strip at 4 columns so the
            Avg $/sqft chip fits cleanly). */}
        {hasDeals && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="dashboard-secondary-kpis">
            <KpiCard
              label={t("dashboard.kpis.avg_score")}
              value={formatScore(summary?.avg_deal_score ?? 0)}
              sub={`${summary?.best_verdict_count ?? 0} ${t("dashboard.kpis.best_verdict").toLowerCase()} · all deal types`}
              testid="kpi-avg-score"
              spark={user?.is_admin ? trends?.signups_7d : trends?.ai_cost_30d_usd}
            />
          </div>
        )}

        {/* Phase 36 Batch X — Global Multi-Currency Investment Analysis
            KPI strip. Renders ONLY when the user has at least one
            cross-currency deal (`summary.fx_in_play`); a USD-only
            investor sees the original Dashboard untouched. */}
        {summary?.fx_in_play && hasDeals ? (
          <div
            className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
            data-testid="phase36-x-fx-kpis"
          >
            <KpiCard
              label={`Portfolio value (${summary?.base_currency || "USD"})`}
              value={fmt.money(summary?.total_market_value_base ?? 0)}
              sub="FX-adjusted total"
              testid="kpi-fx-market-value"
            />
            <KpiCard
              label="Currency gain"
              value={fmt.money(summary?.currency_gain_base ?? 0)}
              tone={(summary?.currency_gain_base ?? 0) >= 0 ? "good" : "bad"}
              sub={
                summary?.avg_currency_risk_bucket
                  ? `In ${summary?.base_currency || "USD"} • Avg currency risk: ${summary.avg_currency_risk_bucket}`
                  : `In ${summary?.base_currency || "USD"} from FX moves`
              }
              testid="kpi-fx-currency-gain"
            />
            <KpiCard
              label="Adjusted ROI"
              value={fmt.percent(summary?.adjusted_roi_pct ?? 0)}
              tone={(summary?.adjusted_roi_pct ?? 0) >= 0 ? "good" : "bad"}
              sub="Weighted by purchase value"
              testid="kpi-fx-adjusted-roi"
            />
          </div>
        ) : null}

        {/* Phase 19 — Portfolio Appreciation KPI strip (only when user has ≥1 owned deal).
            Phase 36 — Dashboard P0 follow-up: 4-card strip — Appreciation,
            Owned cash flow, Owned count, Still analyzing — so each metric
            has its own headline number (no "1 vs 5" label confusion). */}
        {ownedCount > 0 ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="phase19-owned-kpis">
            <KpiCard
              label="Portfolio appreciation"
              value={
                hasAppreciationData
                  ? portfolioAppreciation >= 0
                    ? `+${fmt.money(portfolioAppreciation)}`
                    : `−${fmt.money(Math.abs(portfolioAppreciation))}`
                  : "—"
              }
              sub={
                !hasAppreciationData
                  ? `Historical data syncing — ${ownedCount} owned ${ownedCount === 1 ? "property" : "properties"}`
                  : portfolioAppreciationPct == null
                    ? `${ownedCount} owned ${ownedCount === 1 ? "property" : "properties"}`
                    : `${(portfolioAppreciationPct * 100).toFixed(1)}% across ${ownedCount} owned`
              }
              tone={hasAppreciationData && portfolioAppreciation >= 0 ? "good" : hasAppreciationData ? "bad" : undefined}
              testid="kpi-portfolio-appreciation"
            />
            <KpiCard
              label="Owned monthly cash flow"
              value={fmt.money(ownedMonthlyCashFlow, { cents: true })}
              tone={ownedMonthlyCashFlow >= 0 ? "good" : "bad"}
              sub={`Across ${ownedCount} owned ${ownedCount === 1 ? "property" : "properties"}`}
              testid="kpi-owned-cashflow"
            />
            <KpiCard
              label="Owned properties"
              value={ownedCount}
              sub={ownedCount === 1 ? "1 property in your portfolio" : `${ownedCount} properties in your portfolio`}
              testid="kpi-owned-count"
            />
            <KpiCard
              label="Still analyzing"
              value={analyzingCount}
              sub={analyzingCount === 1 ? "1 deal in review" : `${analyzingCount} deals in review`}
              testid="kpi-analyzing-count"
            />
          </div>
        ) : null}

        {!hasDeals ? (
          <EmptyState onCta={() => navigate("/deals/new")} t={t} />
        ) : (
          <>
            {mostRecentAi && (
              <button
                type="button"
                onClick={() => navigate(`/deals/${mostRecentAi.id}`)}
                data-testid="ai-insights-card"
                className="group block w-full overflow-hidden rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-white p-6 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="inline-flex items-center gap-1.5 rounded-full bg-indigo-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">
                      <Sparkles className="h-3 w-3" /> Latest AI insight
                    </div>
                    <p className="mt-3 truncate text-xs text-slate-500">
                      {mostRecentAi.property?.address}
                    </p>
                    <h3 className="mt-1 text-lg font-semibold text-slate-900 sm:text-xl">
                      {mostRecentAi.ai_summary.headline}
                    </h3>
                  </div>
                  <div className="flex items-center gap-3">
                    <VerdictBadge verdict={mostRecentAi.ai_summary.verdict} size="lg" />
                    <ArrowRight className="h-4 w-4 text-indigo-500 transition-transform group-hover:translate-x-0.5" />
                  </div>
                </div>
              </button>
            )}

            <PipelineGlanceCard />

            {/* Batch BC — viral loop fixture for authenticated users.
                Sits above the Recent Deals table so it spans the same
                width (no separate sidebar). Polls /api/me/referral-stats
                every 30s so the count ticks up the moment a friend
                signs up via the user's invite link. */}
            <ReferFriendsWidget />

            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="recent-deals-card">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-6 py-4">
              <div>
                <h2 className="text-base font-semibold text-slate-900">{t("dashboard.recent_deals")}</h2>
                <p className="mt-0.5 text-xs text-slate-500">Most recent first · click View to see the full breakdown.</p>
              </div>
              {/* Phase 19 — owner filter chips. Hidden when user has zero owned deals. */}
              {ownedCount > 0 ? (
                <div
                  className="inline-flex rounded-full border border-slate-200 bg-slate-50 p-0.5"
                  role="group"
                  data-testid="dashboard-owner-filter"
                >
                  {[
                    { id: "all", label: "All" },
                    { id: "owned", label: "Owned" },
                    { id: "analyzing", label: "Analyzing" },
                  ].map((opt) => {
                    const active = ownerFilter === opt.id;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setOwnerFilter(opt.id)}
                        data-testid={`filter-chip-${opt.id}`}
                        data-active={active ? "true" : "false"}
                        className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                          active
                            ? "bg-white text-indigo-700 shadow-sm"
                            : "text-slate-600 hover:text-slate-900"
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>

            {/* Phase 19 — filter the displayed list by ownerFilter. */}
            {(() => {
              const filtered = (deals || []).filter((d) => {
                if (ownerFilter === "owned") return !!d?.is_owned;
                if (ownerFilter === "analyzing") return !d?.is_owned;
                return true;
              });
              return (
                <>
                  {filtered.length === 0 ? (
                    <div className="px-6 py-10 text-center text-sm text-slate-500" data-testid="filter-empty-state">
                      No deals match the “{ownerFilter}” filter.
                    </div>
                  ) : null}
                  {/* Desktop table */}
                  <div className="hidden md:block">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t("dashboard.table.address")}</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead className="text-right">{t("dashboard.table.cap_rate") === "Cap rate" ? "Price" : "Price"}</TableHead>
                          <TableHead className="text-right">{t("dashboard.table.cash_flow")} / mo</TableHead>
                          <TableHead className="text-right">{t("dashboard.table.cap_rate")}</TableHead>
                          <TableHead className="text-right">CoC</TableHead>
                          <TableHead className="text-right">{t("dashboard.table.score")}</TableHead>
                          {/* Phase 36 Batch P — Payback column. Pulls from
                              role-aware metrics_at_role.payback_period_years.
                              Renders "Never" when annual CF ≤ 0. */}
                          <TableHead className="text-right">Payback</TableHead>
                          <TableHead>{t("dashboard.table.verdict")}</TableHead>
                          <TableHead className="text-right">{t("common.open")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filtered.slice(0, 10).map((d) => (
                          <TableRow
                            key={d.id}
                            data-testid={`deal-row-${d.id}`}
                            data-owned={d?.is_owned ? "true" : "false"}
                            className="cursor-pointer transition-colors hover:bg-slate-50"
                            onClick={() => navigate(`/deals/${d.id}`)}
                          >
                            <TableCell className="font-medium text-slate-900">
                              <div className="flex flex-wrap items-center gap-2">
                                {/* Phase 36 Batch K — role indicator dot (buyer indigo / seller amber) */}
                                <RoleDot
                                  metrics={d.metrics_at_role}
                                  role={d.role}
                                  testid={`role-dot-${d.id}`}
                                />
                                <span>{d.property?.address || "—"}</span>
                                <DealTypePill type={d.deal_type} size="sm" testid={`deal-type-pill-${d.id}`} />
                                {/* Phase 36 P0 follow-up — stage chip is
                                    ALWAYS editable, including for owned
                                    deals (so user can transition
                                    owned → sold / negotiating). Falls
                                    back to "owned" / "prospect" when
                                    the deal has no pipeline_stage set
                                    (legacy / unbackfilled deals). The
                                    wrapper stops row-click navigation
                                    when the user opens the dropdown. */}
                                <span onClick={(e) => e.stopPropagation()}>
                                  <StageBadge
                                    stage={d.pipeline_stage || (d.is_owned ? "owned" : "prospect")}
                                    dealId={d.id}
                                    editable
                                    size="sm"
                                  />
                                </span>
                                {d?.is_owned && d?.purchase_year ? (
                                  <span
                                    data-testid={`owned-badge-${d.id}`}
                                    className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 ring-1 ring-emerald-200"
                                  >
                                    {d.purchase_year === new Date().getFullYear() ? "Just purchased" : `Owned since ${d.purchase_year}`}
                                  </span>
                                ) : null}
                              </div>
                              <PropertyDetailLine d={d} fmt={fmt} />
                              <PropertyDescription d={d} fmt={fmt} />
                              <PropertyContactLine d={d} />
                            </TableCell>
                            <TableCell className="text-slate-700">{propertyTypeLabel(d.property?.property_type)}</TableCell>
                            {/* Phase 36 Batch M Bug 3 — price column reflects
                                role + ownership state. display_price is
                                purchase_price_paid for owned deals, counter
                                for buyer-w/-counter, asking otherwise. Tiny
                                subscript label tells the user which one. */}
                            <TableCell className="text-right tabular-nums">
                              <div>{fmt.money(d.metrics_at_role?.display_price ?? d.purchase_price)}</div>
                              {d.metrics_at_role?.display_price_label ? (
                                <div className="text-[10px] uppercase tracking-wider text-slate-400">
                                  {d.metrics_at_role.display_price_label}
                                </div>
                              ) : null}
                              {/* Phase 47.2 — compact $/sqft subtitle.
                                  PSM omitted here to keep the table
                                  column narrow; dual-display lives in
                                  detail views. */}
                              {(() => {
                                const psf = formatPsfShort(
                                  d.metrics_at_role?.display_price ?? d.purchase_price,
                                  d.property?.square_feet,
                                );
                                return psf ? (
                                  <div
                                    data-testid={`price-psf-${d.id}`}
                                    className="text-[10px] tabular-nums text-slate-400"
                                  >
                                    {psf}
                                  </div>
                                ) : null;
                              })()}
                            </TableCell>
                            {/* Phase 36 Batch K — every numeric column reads from
                                metrics_at_role (with legacy fallback) so the
                                numbers honour each row's price basis.
                                Phase 45 Batch — flip rows render em-dashes
                                for rental-only fields (CF/Cap/CoC/Payback)
                                because flip economics live on ROI + Gross
                                Profit, not on those rental fields. */}
                            <TableCell
                              className={`text-right tabular-nums font-medium ${
                                d.deal_type === "flip"
                                  ? ""
                                  : (d.metrics_at_role?.monthly_cash_flow ?? d.cash_flow_monthly) >= 0
                                    ? "text-emerald-700"
                                    : "text-red-600"
                              }`}
                              data-testid={`cf-cell-${d.id}`}
                            >
                              {d.deal_type === "flip" ? (
                                <FlipDash testid={`flip-dash-cf-${d.id}`} />
                              ) : (
                                fmt.money(d.metrics_at_role?.monthly_cash_flow ?? d.cash_flow_monthly, { cents: true })
                              )}
                            </TableCell>
                            <TableCell
                              className="text-right tabular-nums"
                              data-testid={`cap-cell-${d.id}`}
                            >
                              {d.deal_type === "flip" ? (
                                <FlipDash testid={`flip-dash-cap-${d.id}`} />
                              ) : (
                                fmt.percent(d.metrics_at_role?.cap_rate ?? d.cap_rate)
                              )}
                            </TableCell>
                            <TableCell
                              className={`text-right tabular-nums ${
                                d.deal_type === "flip"
                                  ? ""
                                  : (d.metrics_at_role?.cash_on_cash ?? d.cash_on_cash ?? 0) >= 0
                                    ? ""
                                    : "text-red-600"
                              }`}
                              data-testid={`coc-cell-${d.id}`}
                            >
                              {d.deal_type === "flip" ? (
                                <FlipDash testid={`flip-dash-coc-${d.id}`} />
                              ) : (
                                fmt.percent(d.metrics_at_role?.cash_on_cash ?? d.cash_on_cash)
                              )}
                            </TableCell>
                            <TableCell className="text-right tabular-nums font-semibold">
                              {formatScore(d.metrics_at_role?.deal_score ?? d.deal_score)}
                            </TableCell>
                            {/* Phase 36 Batch P — Payback cell. "Never"
                                when annual CF ≤ 0; finite years
                                otherwise. Phase 45 — flips show em-dash
                                because payback is a rental concept. */}
                            <TableCell
                              className="text-right tabular-nums"
                              data-testid={`payback-cell-${d.id}`}
                            >
                              {d.deal_type === "flip" ? (
                                <FlipDash testid={`flip-dash-payback-${d.id}`} />
                              ) : d.metrics_at_role?.payback_period_years == null ? (
                                <span className="text-slate-400">Never</span>
                              ) : (
                                <span className="text-slate-700">
                                  {Number(d.metrics_at_role.payback_period_years).toFixed(1)}
                                  <span className="ml-0.5 text-[10px] text-slate-400">yrs</span>
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              {/* Phase 36 Batch N — list verdict is the
                                  LIVE role-aware verdict (recomputed at
                                  counter for buyer-w/-counter, asking
                                  for sellers, etc.). Fall back to the
                                  stored verdict only when the new block
                                  hasn't loaded yet. */}
                              <VerdictBadge verdict={d.metrics_at_role?.verdict ?? d.verdict} />
                            </TableCell>
                            <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                              <div className="inline-flex items-center gap-2">
                                <Button
                                  data-testid={`view-deal-${d.id}`}
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => navigate(`/deals/${d.id}`)}
                                  className="text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700"
                                >
                                  {t("common.open")} <ArrowRight className="ml-1 h-3.5 w-3.5" />
                                </Button>
                                <Button
                                  data-testid={`delete-deal-${d.id}`}
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => setPendingDeleteId(d.id)}
                                  className="text-slate-400 hover:bg-red-50 hover:text-red-600"
                                  aria-label={t("common.delete")}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Mobile cards */}
                  <div className="md:hidden">
                    <ul className="divide-y divide-slate-200">
                      {filtered.slice(0, 10).map((d) => (
                        <li
                          key={`m-${d.id}`}
                          data-testid={`deal-row-mobile-${d.id}`}
                          data-owned={d?.is_owned ? "true" : "false"}
                          className="cursor-pointer p-4 active:bg-slate-50"
                          onClick={() => navigate(`/deals/${d.id}`)}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-1.5">
                                {/* Phase 36 Batch K — role indicator dot on the mobile card */}
                                <RoleDot
                                  metrics={d.metrics_at_role}
                                  role={d.role}
                                  testid={`role-dot-mobile-${d.id}`}
                                />
                                <p className="truncate text-sm font-semibold text-slate-900">{d.property?.address}</p>
                                <DealTypePill type={d.deal_type} size="sm" testid={`deal-type-pill-mobile-${d.id}`} />
                                {/* Phase 36 P0 follow-up — editable stage chip
                                    on the mobile card too, with same fallback
                                    as the desktop table so owned / unbackfilled
                                    deals still get a dropdown. */}
                                <span onClick={(e) => e.stopPropagation()}>
                                  <StageBadge
                                    stage={d.pipeline_stage || (d.is_owned ? "owned" : "prospect")}
                                    dealId={d.id}
                                    editable
                                    size="sm"
                                  />
                                </span>
                                {d?.is_owned && d?.purchase_year ? (
                                  <span className="inline-flex items-center rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 ring-1 ring-emerald-200">
                                    {d.purchase_year === new Date().getFullYear() ? "Just purchased" : `Owned · ${d.purchase_year}`}
                                  </span>
                                ) : null}
                              </div>
                              <PropertyDetailLine d={d} fmt={fmt} />
                              <PropertyDescription d={d} fmt={fmt} />
                              <PropertyContactLine d={d} />
                            </div>
                            <VerdictBadge verdict={d.metrics_at_role?.verdict ?? d.verdict} />
                          </div>
                          <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                            <div><span className="text-slate-500">CF/mo</span>
                              <div className={`mt-0.5 font-medium tabular-nums ${
                                d.deal_type === "flip"
                                  ? "text-slate-400"
                                  : (d.metrics_at_role?.monthly_cash_flow ?? d.cash_flow_monthly) >= 0
                                    ? "text-emerald-700"
                                    : "text-red-600"
                              }`}>
                                {d.deal_type === "flip"
                                  ? <FlipDash testid={`flip-dash-cf-mobile-${d.id}`} />
                                  : fmt.money(d.metrics_at_role?.monthly_cash_flow ?? d.cash_flow_monthly)}
                              </div>
                            </div>
                            <div><span className="text-slate-500">Cap</span>
                              <div className="mt-0.5 font-medium tabular-nums text-slate-900">
                                {d.deal_type === "flip"
                                  ? <FlipDash testid={`flip-dash-cap-mobile-${d.id}`} />
                                  : fmt.percent(d.metrics_at_role?.cap_rate ?? d.cap_rate)}
                              </div>
                            </div>
                            <div><span className="text-slate-500">Score</span><div className="mt-0.5 font-semibold tabular-nums text-slate-900">{formatScore(d.metrics_at_role?.deal_score ?? d.deal_score)}</div></div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              );
            })()}
            </div>
          </>
        )}
      </div>
      </TooltipProvider>

      <AlertDialog open={!!pendingDeleteId} onOpenChange={(o) => !o && setPendingDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this deal?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the deal from your workspace. This action can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="dash-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              disabled={deleting}
              data-testid="dash-confirm-delete"
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {deleting ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
